(() => {
  if (window.__vccAutoCollectionFeedBootstrapLoaded) {
    window.__vccDiscoverAutoCollectionFeeds?.();
    return;
  }
  window.__vccAutoCollectionFeedBootstrapLoaded = true;

  const ROOT_SELECTOR = "[data-vcc-feed]";
  const HERO_PAINT_EVENT = "vcc:first-hero-painted";
  const PROXIMITY_MARGIN = "700px 0px";
  const INTENT_EVENTS = ["pointerover", "pointerdown", "touchstart", "focusin", "keydown", "click"];
  const CUSTOMER_ENGAGEMENT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];
  const ACTIONABLE_SELECTOR = "a[href], button, input, select, textarea, [role='button'], [data-vcc-goto], [data-vcc-next], [data-vcc-prev]";
  const states = new Map();
  const assetPromises = new Map();
  let heroPainted = window.__vccFirstHeroPainted === true
    || document.documentElement.dataset.vccFirstHeroPainted === "true";

  function recordCustomerEngagement(event) {
    if (window.__vccCustomerEngaged === true || event.isTrusted === false) return;
    window.__vccCustomerEngaged = true;
    CUSTOMER_ENGAGEMENT_EVENTS.forEach((eventName) => {
      document.removeEventListener(eventName, recordCustomerEngagement, true);
    });
    document.dispatchEvent(new CustomEvent("vcc:customer-engaged", { detail: { type: event.type } }));
  }

  if (window.__vccCustomerEngaged !== true) {
    CUSTOMER_ENGAGEMENT_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, recordCustomerEngagement, { capture: true, passive: true });
    });
  }

  const proximityObserver = typeof window.IntersectionObserver === "function"
    ? new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const state = states.get(entry.target);
          if (!state) return;
          state.near = true;
          if (heroPainted && !state.autoRetryBlocked) requestApplication(entry.target, state, "hero-painted-and-near");
        });
      }, { rootMargin: PROXIMITY_MARGIN, threshold: 0.01 })
    : null;

  function fullAsset(type, url) {
    if (!url) return Promise.reject(new Error(`Feature Collection ${type} URL is unavailable.`));
    const key = `${type}:${url}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      if (type === "style") {
        let existing = Array.from(document.querySelectorAll("link[rel='stylesheet']"))
          .find((node) => node.href === new URL(url, document.baseURI).href);
        if (existing?.dataset.vccFeedLoadFailed === "true") {
          if (existing.dataset.vccFeedFullStyle !== undefined) existing.remove();
          existing = null;
        }
        if (existing?.sheet || existing?.dataset.vccFeedLoaded === "true") {
          resolve(existing);
          return;
        }
        const link = existing || document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        link.dataset.vccFeedFullStyle = "";
        link.addEventListener("load", () => {
          link.dataset.vccFeedLoaded = "true";
          resolve(link);
        }, { once: true });
        link.addEventListener("error", () => {
          link.dataset.vccFeedLoadFailed = "true";
          if (link.dataset.vccFeedFullStyle !== undefined) link.remove();
          reject(new Error("Feature Collection stylesheet failed to load."));
        }, { once: true });
        if (!existing) document.head.append(link);
        return;
      }

      if (typeof window.__vccMountAutoCollectionFeed === "function") {
        resolve(null);
        return;
      }
      let existing = Array.from(document.querySelectorAll("script[src]"))
        .find((node) => node.src === new URL(url, document.baseURI).href);
      if (existing?.dataset.vccFeedLoadFailed === "true") {
        if (existing.dataset.vccFeedFullScript !== undefined) existing.remove();
        existing = null;
      }
      if (existing?.dataset.vccFeedLoaded === "true") {
        resolve(existing);
        return;
      }
      const script = existing || document.createElement("script");
      script.src = url;
      script.defer = true;
      script.dataset.vccFeedFullScript = "";
      script.addEventListener("load", () => {
        script.dataset.vccFeedLoaded = "true";
        resolve(script);
      }, { once: true });
      script.addEventListener("error", () => {
        script.dataset.vccFeedLoadFailed = "true";
        if (script.dataset.vccFeedFullScript !== undefined) script.remove();
        reject(new Error("Feature Collection application failed to load."));
      }, { once: true });
      if (!existing) document.head.append(script);
    }).catch((error) => {
      assetPromises.delete(key);
      throw error;
    });

    assetPromises.set(key, promise);
    return promise;
  }

  function actionableTarget(event, section) {
    const target = event.target instanceof Element ? event.target.closest(ACTIONABLE_SELECTOR) : null;
    return target && section.contains(target) ? target : null;
  }

  function isActivationKey(event) {
    return event.key === "Enter" || event.key === " " || event.key === "Spacebar";
  }

  function queueAction(event, section, state) {
    const target = actionableTarget(event, section);
    if (!target) return;
    if (event.type === "keydown" && !isActivationKey(event)) return;
    if (event.type !== "click" && event.type !== "keydown") return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.pendingAction) state.pendingAction = { target };
  }

  function replayAction(state) {
    const action = state.pendingAction;
    state.pendingAction = null;
    if (!action?.target?.isConnected || typeof action.target.click !== "function") return;
    state.replaying = true;
    try {
      action.target.click();
    } finally {
      state.replaying = false;
    }
  }

  function unbindIntent(section, state) {
    if (!state.intentHandler) return;
    INTENT_EVENTS.forEach((eventName) => section.removeEventListener(eventName, state.intentHandler, true));
    state.intentHandler = null;
  }

  function markReady(section, state) {
    if (state.ready) return;
    state.ready = true;
    section.dataset.vccFeedBootstrapReady = "true";
    section.removeAttribute("aria-busy");
    unbindIntent(section, state);
    if (proximityObserver) proximityObserver.unobserve(section);
    replayAction(state);
  }

  function failOpen(section, state, error) {
    state.loading = false;
    state.promise = null;
    state.autoRetryBlocked = true;
    section.dataset.vccFeedLoadError = "true";
    section.removeAttribute("aria-busy");
    console.error("[VCC Feature Collection bootstrap]", error);

    const action = state.pendingAction;
    state.pendingAction = null;
    if (proximityObserver && section.isConnected) proximityObserver.observe(section);
    if (!action) return;
    const directUrl = action?.target?.closest?.("a[href]")?.href;
    if (directUrl) {
      window.location.assign(directUrl);
      return;
    }

    const data = section.querySelector("[data-vcc-feed-data]");
    try {
      const firstCollectionUrl = JSON.parse(data?.textContent || "{}").blocks?.[0]?.url;
      if (firstCollectionUrl && firstCollectionUrl !== "#") window.location.assign(firstCollectionUrl);
    } catch (_error) {
      // The stable shell remains available and a later intent may retry after navigation/reload.
    }
  }

  function requestApplication(section, state = states.get(section), reason = "intent") {
    if (!state || state.ready || state.loading) return state?.promise;
    if (reason.startsWith("intent:")) state.autoRetryBlocked = false;
    state.loading = true;
    section.removeAttribute("data-vcc-feed-load-error");
    section.dataset.vccFeedLoadReason = reason;
    section.setAttribute("aria-busy", "true");
    if (proximityObserver) proximityObserver.unobserve(section);

    const cssUrl = section.dataset.vccFeedCssSrc;
    const scriptUrl = section.dataset.vccFeedScriptSrc;
    state.promise = Promise.all([
      fullAsset("style", cssUrl),
      fullAsset("script", scriptUrl),
    ]).then(() => {
      if (state.cancelled || !section.isConnected) return;
      section.dataset.vccFeedAssetsReady = "true";
      if (typeof window.__vccMountAutoCollectionFeed !== "function") {
        throw new Error("Feature Collection mount API is unavailable after asset load.");
      }
      window.__vccMountAutoCollectionFeed(section);
      if (section.dataset.vccFeedReady === "true") markReady(section, state);
    }).catch((error) => failOpen(section, state, error));
    return state.promise;
  }

  function bindIntent(section, state) {
    state.intentHandler = (event) => {
      if (state.ready || state.replaying || event.isTrusted === false) return;
      section.dataset.vccFeedIntentEngaged = "true";
      queueAction(event, section, state);
      requestApplication(section, state, `intent:${event.type}`);
    };
    INTENT_EVENTS.forEach((eventName) => {
      const passive = eventName === "pointerover" || eventName === "pointerdown" || eventName === "touchstart";
      section.addEventListener(eventName, state.intentHandler, { capture: true, passive });
    });
  }

  function setup(section) {
    if (!(section instanceof Element) || states.has(section)) return;
    const state = {
      near: !proximityObserver,
      loading: false,
      ready: section.dataset.vccFeedReady === "true",
      cancelled: false,
      replaying: false,
      pendingAction: null,
      intentHandler: null,
      promise: null,
      autoRetryBlocked: false,
    };
    states.set(section, state);
    if (state.ready) return;
    bindIntent(section, state);

    if (window.Shopify?.designMode) {
      requestApplication(section, state, "theme-editor");
      return;
    }
    if (proximityObserver) proximityObserver.observe(section);
    else if (heroPainted) requestApplication(section, state, "hero-painted-no-observer");
  }

  function discover(root = document) {
    const sections = [
      ...(root.matches?.(ROOT_SELECTOR) ? [root] : []),
      ...root.querySelectorAll(ROOT_SELECTOR),
    ];
    sections.forEach(setup);
  }

  function recordHeroPainted() {
    if (heroPainted) return;
    heroPainted = true;
    states.forEach((state, section) => {
      if (state.near && !state.ready && !state.autoRetryBlocked) {
        requestApplication(section, state, "hero-painted-and-near");
      }
    });
  }

  function unload(root) {
    const sections = [
      ...(root.matches?.(ROOT_SELECTOR) ? [root] : []),
      ...root.querySelectorAll(ROOT_SELECTOR),
    ];
    sections.forEach((section) => {
      const state = states.get(section);
      if (!state) return;
      state.cancelled = true;
      unbindIntent(section, state);
      if (proximityObserver) proximityObserver.unobserve(section);
      states.delete(section);
    });
  }

  window.__vccDiscoverAutoCollectionFeeds = discover;
  document.addEventListener(HERO_PAINT_EVENT, recordHeroPainted, { once: true });
  window.addEventListener(HERO_PAINT_EVENT, recordHeroPainted, { once: true });
  document.addEventListener("vcc:feed-ready", (event) => {
    const section = event.detail?.section;
    const state = states.get(section);
    if (state) markReady(section, state);
  });
  document.addEventListener("shopify:section:load", (event) => discover(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => discover(), { once: true });
  } else {
    discover();
  }
})();
